import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { UserProfile } from '../types';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserService } from '../services/userService';

interface UserContextType {
  user: User | null;
  profile: UserProfile | null;
  refreshProfile: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

interface UserProviderProps {
  user: User | null;
  profile: UserProfile | null;
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ user, profile: initialProfile, children }) => {
  const [profile, setProfile] = useState<UserProfile | null>(initialProfile);

  // Set up real-time listener for profile updates
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }

    console.log('🔄 Setting up profile listener for user:', user.uid);
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'userProfiles', user.uid),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const updatedProfile: UserProfile = {
            uid: data.uid,
            email: data.email,
            isAdmin: data.isAdmin || false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            ...(data.displayName && { displayName: data.displayName }),
            ...(data.handle && { handle: data.handle }),
            ...(data.bio && { bio: data.bio }),
            ...(data.profilePicUrl && { profilePicUrl: data.profilePicUrl }),
          };
          console.log('✅ Profile updated in real-time:', updatedProfile);
          setProfile(updatedProfile);
        }
      },
      (error) => {
        console.error('❌ Error listening to profile updates:', error);
      }
    );

    return () => {
      console.log('🔚 Cleaning up profile listener');
      unsubscribe();
    };
  }, [user]);

  // Update profile when initialProfile changes (e.g., on login)
  useEffect(() => {
    setProfile(initialProfile);
  }, [initialProfile]);

  // Manual refresh function
  const refreshProfile = async () => {
    if (!user) return;
    
    console.log('🔄 Manual profile refresh requested');
    try {
      const updatedProfile = await UserService.getUserProfile(user.uid);
      if (updatedProfile) {
        setProfile(updatedProfile);
        console.log('✅ Profile manually refreshed:', updatedProfile);
      }
    } catch (error) {
      console.error('❌ Error refreshing profile:', error);
    }
  };

  return (
    <UserContext.Provider value={{ user, profile, refreshProfile }}>
      {children}
    </UserContext.Provider>
  );
};